import endpoints from '@/api/endpoints';
import { http, HttpResponse } from 'msw';

const API_BASE_URL = endpoints.baseURL;

const TEST_USERNAME = 'usertTest';
const TEST_PASSWORD = 'Toto@1234';
const TEST_USERNAME_DIRECT = 'usertTestDirect'; // phoneValidated=true, goes straight to dashboard
const TEST_USERNAME_SSP = 'usertTestSsp'; // phoneValidated=true, has two SSP accounts
const TEST_PHONE = '+216XXXXXXX5248'; // Masked for security
const TEST_PHONE_UNMASKED = '+21698765432';
const TEST_DECODEABLE_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NlcHRlZFRlcm1zSWQiOiJ0ZXJtcy12MSIsImxhdGVzdFRlcm1zIjp7ImlkIjoidGVybXMtdjEiLCJlZmZlY3RpdmVEYXRlIjoiMjAyNC0wMS0wMVQwMDowMDowMFoifSwiZXhwIjo0MTAyNDQ0ODAwfQ.signature';
const TEST_SSP_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2NlcHRlZFRlcm1zSWQiOiJ0ZXJtcy12MSIsImxhdGVzdFRlcm1zIjp7ImlkIjoidGVybXMtdjEiLCJlZmZlY3RpdmVEYXRlIjoiMjAyNC0wMS0wMVQwMDowMDowMFoifSwiZXhwIjo0MTAyNDQ0ODAwfQ.ssp_signature';

export { TEST_SSP_TOKEN };

export const loginHandler = http.post<object, any, any>(`${API_BASE_URL}${endpoints.login}`, async ({ request }) => {
  try {
    let username, password;

    const contentType = request.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const body = await request.json();
      username = body.username;
      password = body.password;
    } else {
      const formData = await request.formData();
      username = formData.get('username');
      password = formData.get('password');
    }

    if (username === TEST_USERNAME && password === TEST_PASSWORD) {
      return HttpResponse.json({
        accessToken: TEST_DECODEABLE_TOKEN,
        phone: TEST_PHONE,
        phoneValidated: false,
        mfaEnabled: false,
        mfaType: null,
        isFirstPasswordChanged: false,
      });
    }

    if (username === TEST_USERNAME_DIRECT && password === TEST_PASSWORD) {
      return HttpResponse.json({
        accessToken: TEST_DECODEABLE_TOKEN,
        phone: TEST_PHONE_UNMASKED,
        phoneValidated: true,
        mfaEnabled: false,
        mfaType: null,
        isFirstPasswordChanged: true,
      });
    }

    if (username === TEST_USERNAME_SSP && password === TEST_PASSWORD) {
      return HttpResponse.json({
        accessToken: TEST_SSP_TOKEN,
        phone: TEST_PHONE_UNMASKED,
        phoneValidated: true,
        mfaEnabled: false,
        mfaType: null,
        isFirstPasswordChanged: true,
      });
    }

    return HttpResponse.json(
      {
        code: 'login_credentials_incorrect',
        message: {
          en: "We're sorry, but we couldn't log you in with the details provided. Please check your username and password and try again.",
          fr: "Nous sommes désolés, nous ne pouvons pas vous connecter avec les détails fournis. Veuillez vérifier votre nom d'utilisateur et votre mot de passe et réessayer.",
        },
      },
      { status: 401 }
    );
  } catch (error) {
    return HttpResponse.json(
      {
        code: 'invalid_request',
        message: {
          en: 'Invalid request format',
          fr: 'Format de demande invalide',
        },
      },
      { status: 400 }
    );
  }
});

export const phoneValidateHandler = http.post<object, any, any>(`${API_BASE_URL}${endpoints.validatePhone}`, async ({ request }) => {
  try {
    const body = await request.json();
    const { phone } = body;

    if (phone === TEST_PHONE_UNMASKED || phone === '+216XXXXXXX5248' || phone === ' ') {
      return HttpResponse.json({
        message: {
          en: 'Verification code sent to your phone',
          fr: 'Code de vérification envoyé à votre téléphone',
        },
        otpSendMessage: {
          en: 'An SMS with your verification code has been sent',
          fr: 'Un SMS avec votre code de vérification a été envoyé',
        },
        isOtpBlocked: false,
        phone: TEST_PHONE,
      });
    }

    return HttpResponse.json(
      {
        code: 'invalid_phone_format',
        message: {
          en: 'Invalid phone number format',
          fr: 'Format de numéro de téléphone invalide',
        },
      },
      { status: 400 }
    );
  } catch (error) {
    return HttpResponse.json(
      {
        code: 'invalid_request',
        message: {
          en: 'Invalid request',
          fr: 'Demande invalide',
        },
      },
      { status: 400 }
    );
  }
});

export const verifyOtpHandler = http.post<object, any, any>(`${API_BASE_URL}${endpoints.verifyOtp}`, async ({ request }) => {
  try {
    const body = await request.json();

    const validOtpCodes = ['123456', '111111'];
    const { code } = body;

    if (validOtpCodes.includes(code)) {
      return HttpResponse.json(
        {
          accessToken: TEST_DECODEABLE_TOKEN,
          mfaEnabled: false,
          isFirstPasswordChanged: true,
          message: {
            en: 'Code verified successfully',
            fr: 'Code vérifié avec succès',
          },
        },
        { status: 200 }
      );
    }

    return HttpResponse.json(
      {
        code: 'invalid_otp_code',
        message: {
          en: 'The verification code you entered is incorrect',
          fr: 'Le code de vérification que vous avez entré est incorrect',
        },
      },
      { status: 401 }
    );
  } catch (error) {
    return HttpResponse.json(
      {
        code: 'invalid_request',
        message: {
          en: 'Invalid request',
          fr: 'Demande invalide',
        },
      },
      { status: 400 }
    );
  }
});

export const otpResendHandler = http.post<object, any, any>(`${API_BASE_URL}${endpoints.otpResend}`, async () => {
  return HttpResponse.json({
    otpSendMessage: {
      en: 'A new verification code has been sent to your phone',
      fr: 'Un nouveau code de vérification a été envoyé à votre téléphone',
    },
    isOtpBlocked: false,
    message: {
      en: 'Code resent successfully',
      fr: 'Code renvoyé avec succès',
    },
  });
});

export const updatePasswordHandler = http.post<object, any, any>(`${API_BASE_URL}/auth/password`, async ({ request }) => {
  try {
    const body = await request.json();

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const { newPassword } = body;

    if (!passwordRegex.test(newPassword)) {
      return HttpResponse.json(
        {
          code: 'password_too_weak',
          message: {
            en: 'Password must be at least 8 characters with uppercase, lowercase, number and special character',
            fr: 'Le mot de passe doit contenir au moins 8 caractères avec majuscules, minuscules, chiffres et caractères spéciaux',
          },
        },
        { status: 400 }
      );
    }

    return HttpResponse.json({
      accessToken: TEST_DECODEABLE_TOKEN,
      message: {
        en: 'Password changed successfully',
        fr: 'Mot de passe modifié avec succès',
      },
      isFirstPasswordChanged: true,
    });
  } catch (error) {
    return HttpResponse.json(
      {
        code: 'invalid_request',
        message: {
          en: 'Invalid request',
          fr: 'Demande invalide',
        },
      },
      { status: 400 }
    );
  }
});

export const totpInitHandler = http.post<object, any, any>(`${API_BASE_URL}/auth/totp/init-setup`, async ({ request }) => {
  try {
    return HttpResponse.json({
      secret: 'JBSWY3DPEBLW64TMMQ======',
      qrCodeUri: 'otpauth://totp/TestUser?secret=JBSWY3DPEBLW64TMMQ======&issuer=BankApp',
      qrCodeB64: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
    });
  } catch (error) {
    return HttpResponse.json(
      {
        code: 'invalid_request',
        message: {
          en: 'Invalid request',
          fr: 'Demande invalide',
        },
      },
      { status: 400 }
    );
  }
});

export const logoutHandler = http.post<object, any, any>(`${API_BASE_URL}${endpoints.logout}`, async () => {
  return HttpResponse.json({
    message: {
      en: 'Logged out successfully',
      fr: 'Déconnecté avec succès',
    },
  });
});

export const getCurrentUserHandler = http.get<object, any, any>(`${API_BASE_URL}${endpoints.getMe}`, async () => {
  return HttpResponse.json({
    id: '237670707070',
    username: TEST_USERNAME,
    email: 'testuser@example.com',
    phone: TEST_PHONE,
    firstName: 'Test',
    lastName: 'User',
    isSubUser: false,
    isMain: true,
    subscriptionEndDate: '',
    duePaymentDate: '',
    language: 'en',
    phoneValidated: true,
    mfaEnabled: false,
    isFirstPasswordChanged: true,
    permissions: [
      { name: 'VIEW_ACCOUNTS' },
      { name: 'VIEW_TRANSACTIONS' },
      { name: 'VIEW_DASHBOARD' },
      { id: 'ee695fdf-d5f2-46f6-af49-c15c4252500a', name: 'payment_transfers_list_create', type: 'CLIENT' },
      { id: '22e681fb-aef4-46b0-9c08-676075d97ee1', name: 'payment_international-transfers_one_create', type: 'CLIENT' },
      { id: 'fc0087f6-8de4-42b9-ad76-d3f8dd621b20', name: 'payment_transfers_internal-transfer_create', type: 'CLIENT' },
    ],
    packageInfo: {
      description: 'Mock package',
      title: 'Corporate Basic',
      freeOfCharge: true,
      maxNumberSubUsers: 5,
      periodicity: 'MONTHLY',
      permissions: [
        { id: 'ee695fdf-d5f2-46f6-af49-c15c4252500a', name: 'payment_transfers_list_create' },
        { id: '22e681fb-aef4-46b0-9c08-676075d97ee1', name: 'payment_international-transfers_one_create' },
        { id: 'fc0087f6-8de4-42b9-ad76-d3f8dd621b20', name: 'payment_transfers_internal-transfer_create' },
      ],
      pricing: [],
    },
    subscriptionInfo: {
      billingAccountNumber: '1234567890',
      isAutoCharging: false,
    },
    permanentRole: 'ADMIN',
    validatorsCount: 0,
    unreadAlertsCount: 0,
  });
});

export const getAlertsHandler = http.get<object, any, any>(`${API_BASE_URL}${endpoints.getAlerts}`, async () => {
  return HttpResponse.json({
    data: [],
    total: 0,
  });
});

export const getSessionsHandler = http.get<object, any, any>(`${API_BASE_URL}${endpoints.getSesions}`, async () => {
  return HttpResponse.json({
    current: {
      id: 'session-current',
      createdAt: '2026-04-13T10:00:00.000Z',
      ipAddress: '192.168.1.10',
      device: {
        deviceType: 'Desktop',
        browser: 'Chrome',
      },
    },
    others: [
      {
        id: 'session-other-1',
        createdAt: '2026-04-12T08:30:00.000Z',
        ipAddress: '192.168.1.11',
        device: {
          deviceType: 'Laptop',
          browser: 'Firefox',
        },
      },
    ],
  });
});

export const closeOtherSessionsHandler = http.post<object, any, any>(`${API_BASE_URL}${endpoints.closeOtherSessions}`, async () => {
  return HttpResponse.json({
    accessToken: TEST_DECODEABLE_TOKEN,
    message: {
      en: 'Other sessions closed successfully',
      fr: 'Les autres sessions ont ete fermees avec succes',
    },
  });
});

export const authHandlers = [
  loginHandler,
  phoneValidateHandler,
  verifyOtpHandler,
  otpResendHandler,
  updatePasswordHandler,
  totpInitHandler,
  logoutHandler,
  closeOtherSessionsHandler,
  getSessionsHandler,
  getAlertsHandler,
  getCurrentUserHandler,
];
